import { Navbar } from '@/components/Navbar'
import { getClientName, isCcsCheckEnabled } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import NPILookupModal from '@/features/auth/components/NPILookupModal'
import type { NPIResult } from '@/features/auth/hooks/useNPILookup'
import { OrgAdminGuard, useAuthorization } from '@/features/authorization'
import { useOrgNpiAvailability } from '@/hooks/useOrgNpiAvailability'
import {
  EMAIL_MAX_LENGTH,
  FREE_TEXT_INVALID_MESSAGE,
  NPI_INVALID_MESSAGE,
  PHONE_INVALID_MESSAGE,
  PHONE_MAX_LENGTH,
  PHONE_TOO_LONG_MESSAGE,
  ROLE_TITLE_INVALID_MESSAGE,
  ROLE_TITLE_MAX_LENGTH,
  ROLE_TITLE_TOO_LONG_MESSAGE,
  isValidFreeText,
  isValidNpi,
  isValidPhone,
  isValidRoleTitle,
  isWithinLength,
  sanitizeRoleTitle,
  toProperCaseName,
} from '@/utils/freeTextValidation'
import {
  USER_NAME_INVALID_MESSAGE,
  DISPLAY_NAME_MAX_LENGTH,
  MIDDLE_NAME_FIELD_MAX_LENGTH,
  NAME_FIELD_MAX_LENGTH,
  composeDisplayName,
  middleNameFieldLengthError,
  nameFieldLengthError,
  userNameErrorIfChanged,
  isValidUserName,
} from '@/utils/usernameValidation'
import { isValidOrderContactEmail } from '@/utils/contactValidation'
import { parseApiErrorMessage } from '@/utils/parseApiErrorMessage'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import { ChevronDown, Pen } from 'flowbite-react-icons/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// helix-exception: dense admin badges and row actions keep 10-11px text; Helix has no step below text-xs (F-12)

type OrgMember = {
  userId: string
  userName?: string | null
  /** Linked practitioner name parts. The display name is composed from these, not split back out of it. */
  firstName?: string | null
  middleName?: string | null
  lastName?: string | null
  userEmail?: string | null
  membershipId: string
  status: string
  roles: string[]
  createdAt?: string
  delegations?: Delegation[]
  /** Present when the user has an NPI on file (provider-api user list). */
  npiNumber?: string | null
  npi?: string | null
  /** Surfaced via the linked practitioner schema (provider-api user list). */
  phone?: string | null
  /** Practitioner medical_degree, surfaced as roleTitle on the order/TRF. */
  medicalDegree?: string | null
  medical_degree?: string | null
  roleTitle?: string | null
  /**
   * CCS (California Children's Services) eligibility of the linked practitioner.
   * ``null`` when there is no linked practitioner or the org isn't CCS-configured.
   * Only meaningful for clinicians with a linked practitioner (NPI).
   */
  ccsEligible?: boolean | null
}

/** Body for PATCH /auth/users/:id/profile — same shape as PATCH /auth/me (plus admin-only ccsEligible). */
type ProfileUpdateRequest = {
  firstName?: string
  middleName?: string
  lastName?: string
  npi?: string
  phone?: string
  roleTitle?: string
  /**
   * Admin-only tri-state CCS eligibility for the linked practitioner. Omit to
   * leave unchanged; ``true``/``false`` to set explicitly. Not accepted on the
   * self-service PATCH /auth/me endpoint.
   */
  ccsEligible?: boolean
}

function orgMemberNpiDisplay(member: OrgMember): string | null {
  const fromNumber = (member.npiNumber ?? '').trim()
  const fromNpiField = (member.npi ?? '').trim()
  const t = fromNumber || fromNpiField
  return t.length > 0 ? t : null
}

/** Practitioner phone surfaced on the member record (provider-api user list). */
function orgMemberPhone(member: OrgMember): string {
  return (member.phone ?? '').trim()
}

/** Practitioner medical_degree, surfaced as the member's roleTitle / medical title. */
function orgMemberRoleTitle(member: OrgMember): string {
  return (member.roleTitle ?? member.medicalDegree ?? member.medical_degree ?? '').trim()
}

/**
 * Display label for a clinician in delegate-selection dropdowns. When the org is
 * CCS-configured (``ccsEnabled``) and the clinician carries a non-null
 * ``ccsEligible`` value, append a CCS suffix so admins can see eligibility while
 * picking a clinician. A null value (feature off / no linked practitioner) adds
 * no suffix.
 */
function clinicianOptionLabel(c: Clinician, ccsEnabled: boolean): string {
  const base = c.userName || c.userEmail || c.userId
  if (!ccsEnabled || c.ccsEligible == null) return base
  return `${base} — CCS ${c.ccsEligible ? 'eligible' : 'not eligible'}`
}

type Delegation = {
  id: string
  grantorUserId: string
  grantorName?: string | null
  grantorEmail?: string | null
  granteeUserId: string
  permissions: string[]
  status: string
}

type Clinician = {
  userId: string
  userName?: string | null
  userEmail?: string | null
  /**
   * CCS eligibility, present only when the org is configured for the CCS check.
   * ``null`` ⇒ feature off (or not eligible / no linked practitioner). The UI
   * keys the CCS column off whether this comes back non-null.
   */
  ccsEligible?: boolean | null
}

type RoleOption = {
  roleKey: string
  roleName?: string | null
  roleDescription?: string | null
}

/**
 * Case roles are case-scoped and are granted only through the Case Connect
 * manage-access flow (which requires a case) — never through the org-admin user
 * form. The server already filters these out of GET /auth/roles; this client-side
 * filter is belt-and-suspenders so a stray case role could never be selectable.
 */
const CASE_ROLE_KEYS: ReadonlySet<string> = new Set(['case_manager', 'case_writer', 'case_read_only'])

type CreateUserRequest = {
  firstName: string
  middleName: string
  lastName: string
  email: string
  npiNumber: string
  status?: string
  roleKeys: string[]
  delegateForUserId?: string
  phone?: string
  roleTitle?: string
  /**
   * Admin-only CCS eligibility for a new practitioner. Omit to let the server
   * default to false; send ``true``/``false`` to set explicitly. Only meaningful
   * when the user is created with an NPI (linked practitioner).
   */
  ccsEligible?: boolean
}

/** Result of PATCH /auth/users/:id/profile — includes message on failure for inline form errors. */
type AdminProfilePatchResult = { ok: true } | { ok: false; message: string }

export default function Admin() {
  const navigate = useNavigate()
  const { user, logout, getAccessTokenSilently } = useAuth0()
  const { config } = useUiConfig()
  const { userId: currentUserId, hasRole, isLoading: authLoading } = useAuthorization()

  /** Master switch for the CCS UI (admin toggle + clinician column) for this org. */
  const ccsEnabled = isCcsCheckEnabled(config)

  const apiBaseUrl = useMemo(() => {
    return import.meta.env['VITE_PROVIDER_API_URL'] || import.meta.env['VITE_API_BASE_URL']
  }, [])

  const [members, setMembers] = useState<OrgMember[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // Whether the org users list has completed at least one successful fetch.
  // The client-side duplicate-email check compares against `members`, so we must
  // not allow a create until the list has loaded (otherwise a duplicate slips through).
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([])
  const [clinicians, setClinicians] = useState<Clinician[]>([])
  const [delegations, setDelegations] = useState<Delegation[]>([])

  const [addUserForm, setAddUserForm] = useState<CreateUserRequest>({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    npiNumber: '',
    status: 'active',
    roleKeys: [],
    delegateForUserId: undefined,
    phone: '',
    roleTitle: '',
    ccsEligible: false,
  })
  const [isNPIModalOpen, setIsNPIModalOpen] = useState(false)
  const [selectedNPI, setSelectedNPI] = useState<NPIResult | null>(null)
  const [isAddingUser, setIsAddingUser] = useState(false)
  // Synchronous re-entrancy guard: state updates are async, so a rapid second
  // click could pass the `isAddingUser` check before React re-renders. The ref
  // flips immediately, blocking duplicate create requests.
  const isAddingUserRef = useRef(false)

  const [addRolesMenuOpen, setAddRolesMenuOpen] = useState(false)
  const addRolesDetailsRef = useRef<HTMLDetailsElement>(null)

  const addUserNpiAvailability = useOrgNpiAvailability({
    npi: addUserForm.npiNumber,
    baselineNpi: '',
    enabled: true,
  })

  /** Per-field so the error lands on the input that is wrong. */
  const addUserNameFieldError = (
    value: string,
    lengthError: (value: string) => string | undefined = nameFieldLengthError
  ): string | undefined => {
    if (value.length === 0) return undefined
    if (!isValidUserName(value)) return USER_NAME_INVALID_MESSAGE
    return lengthError(value)
  }
  const addUserFirstNameError = addUserNameFieldError(addUserForm.firstName)
  const addUserMiddleNameError = addUserNameFieldError(
    addUserForm.middleName,
    middleNameFieldLengthError
  )
  const addUserLastNameError = addUserNameFieldError(addUserForm.lastName)
  const addUserNameInvalid = Boolean(
    addUserFirstNameError || addUserMiddleNameError || addUserLastNameError
  )

  const addUserEmailInvalid =
    addUserForm.email.trim().length > 0 && !isValidOrderContactEmail(addUserForm.email)

  // Block adding a user whose email already belongs to an existing org member.
  const addUserEmailDuplicate = useMemo(() => {
    const normalized = addUserForm.email.trim().toLowerCase()
    if (!normalized) return false
    return members.some((m) => (m.userEmail ?? '').trim().toLowerCase() === normalized)
  }, [addUserForm.email, members])

  // Trim before validating so a whitespace-only optional NPI is treated as empty (not invalid),
  // matching how `addUser` trims it before submit. Otherwise the button stays stuck disabled.
  const addUserNpiInvalid = !isValidNpi(addUserForm.npiNumber.trim())

  const addUserPhoneInvalid =
    !isValidFreeText(addUserForm.phone ?? '') ||
    !isWithinLength(addUserForm.phone ?? '', PHONE_MAX_LENGTH) ||
    !isValidPhone(addUserForm.phone ?? '')
  const addUserRoleTitleInvalid =
    !isValidFreeText(addUserForm.roleTitle ?? '') ||
    !isValidRoleTitle(addUserForm.roleTitle ?? '') ||
    !isWithinLength(addUserForm.roleTitle ?? '', ROLE_TITLE_MAX_LENGTH)

  useEffect(() => {
    if (!addRolesMenuOpen) return
    const onPointerDown = (e: PointerEvent) => {
      const root = addRolesDetailsRef.current
      if (root && !root.contains(e.target as Node)) {
        setAddRolesMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [addRolesMenuOpen])

  const handleLogout = () => {
    Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Logout from Admin Page',
      },
      () => {
        logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        })
      }
    )
  }

  const authHeaders = async () => {
    const token = await getAccessTokenSilently()
    return {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
  }

  const loadMembers = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`${apiBaseUrl}/auth/users`, {
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to load users (${response.status})`)
      }
      const data = (await response.json()) as OrgMember[]
      setMembers(data)
      setMembersLoaded(true)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load users'
      setError(message)
      // Re-gate the Add user control: on any failed load (initial or a post-create
      // refresh) the `members` list is stale, so the client-side duplicate-email
      // check can't be trusted. Forcing membersLoaded back to false blocks new
      // creates until a successful re-fetch (surfacing the Retry affordance).
      setMembersLoaded(false)
      Sentry.captureException(e, { tags: { operation: 'admin.users.list' } })
    } finally {
      setIsLoading(false)
    }
  }

  const loadRoles = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/roles`, {
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to load roles (${response.status})`)
      }
      const data = (await response.json()) as RoleOption[]
      setRoleOptions(data)
    } catch (e) {
      // Roles are "nice to have" for UI; don't block the page if this fails.
      Sentry.captureException(e, { tags: { operation: 'admin.roles.list' } })
    }
  }

  const loadClinicians = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/users/clinicians`, {
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to load clinicians (${response.status})`)
      }
      const data = (await response.json()) as Clinician[]
      setClinicians(data)
    } catch (e) {
      // Clinicians are for delegation setup; don't block the page if this fails.
      Sentry.captureException(e, { tags: { operation: 'admin.clinicians.list' } })
    }
  }

  const loadDelegations = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/delegations/all`, {
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to load delegations (${response.status})`)
      }
      const data = (await response.json()) as Delegation[]
      setDelegations(data)
    } catch (e) {
      // Delegations are nice-to-have; don't block the page if this fails.
      Sentry.captureException(e, { tags: { operation: 'admin.delegations.list' } })
    }
  }

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    window.setTimeout(() => setSuccessMessage(null), 4500)
  }

  const addUser = async () => {
    if (isAddingUserRef.current) return
    isAddingUserRef.current = true
    setIsAddingUser(true)
    setError(null)
    try {
      const firstName = addUserForm.firstName.trim()
      const middleName = addUserForm.middleName.trim()
      const lastName = addUserForm.lastName.trim()
      const email = addUserForm.email.trim().toLowerCase()
      const npiNumber = addUserForm.npiNumber.trim()
      if (!firstName || !lastName) {
        setError('Please enter a first and last name.')
        return
      }
      const nameError =
        addUserNameFieldError(firstName) ??
        addUserNameFieldError(middleName, middleNameFieldLengthError) ??
        addUserNameFieldError(lastName)
      if (nameError) {
        setError(nameError)
        return
      }
      if (!email) {
        setError('Please enter an email.')
        return
      }
      if (!isValidOrderContactEmail(email)) {
        setError('Please enter a valid email address.')
        return
      }
      if (!membersLoaded) {
        setError(
          isLoading
            ? 'Still loading existing users. Please wait a moment and try again.'
            : 'Couldn’t load existing users. Please retry loading the users list before adding a user.'
        )
        return
      }
      if (members.some((m) => (m.userEmail ?? '').trim().toLowerCase() === email)) {
        setError('A user with this email already exists in the organization.')
        return
      }

      const phone = (addUserForm.phone ?? '').trim()
      const roleTitle = (addUserForm.roleTitle ?? '').trim()
      if (phone && !isValidFreeText(phone)) {
        setError(FREE_TEXT_INVALID_MESSAGE)
        return
      }
      if (!isWithinLength(phone, PHONE_MAX_LENGTH)) {
        setError(PHONE_TOO_LONG_MESSAGE)
        return
      }
      if (phone && !isValidPhone(phone)) {
        setError(PHONE_INVALID_MESSAGE)
        return
      }
      if (roleTitle && !isValidFreeText(roleTitle)) {
        setError(FREE_TEXT_INVALID_MESSAGE)
        return
      }
      if (roleTitle && !isValidRoleTitle(roleTitle)) {
        setError(ROLE_TITLE_INVALID_MESSAGE)
        return
      }
      if (!isWithinLength(roleTitle, ROLE_TITLE_MAX_LENGTH)) {
        setError(ROLE_TITLE_TOO_LONG_MESSAGE)
        return
      }

      // Validate delegate role has clinician selected
      const hasDelegateRole = (addUserForm.roleKeys || []).includes('delegate')
      if (hasDelegateRole && !addUserForm.delegateForUserId) {
        setError('Please select a clinician for the delegate role.')
        return
      }

      if (npiNumber) {
        if (!isValidNpi(npiNumber)) {
          setError(NPI_INVALID_MESSAGE)
          return
        }
        const npiCheck = await addUserNpiAvailability.validateBeforeSubmit()
        if (!npiCheck.ok) {
          setError(npiCheck.message)
          return
        }
      }

      setIsAddingUser(true)
      const createResponse = await fetch(`${apiBaseUrl}/auth/users/create`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          firstName,
          middleName: middleName || undefined,
          lastName,
          email,
          npiNumber: npiNumber || undefined,
          status: addUserForm.status || 'active',
          roleKeys: addUserForm.roleKeys || [],
          delegateForUserId: addUserForm.delegateForUserId || undefined,
          phone: phone || undefined,
          roleTitle: roleTitle || undefined,
          // CCS eligibility only applies to a linked practitioner (NPI) and only
          // when the org is CCS-configured. Otherwise omit so the server keeps
          // its default (false) rather than us forcing a meaningless value.
          ccsEligible: ccsEnabled && npiNumber ? Boolean(addUserForm.ccsEligible) : undefined,
        }),
      })

      if (!createResponse.ok) {
        const message = await parseApiErrorMessage(createResponse)
        // 409: the email belongs to another organization. Expected input, not a fault to report.
        if (createResponse.status === 409) {
          setError(message)
          return
        }
        throw new Error(message)
      }

      setAddUserForm({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        npiNumber: '',
        status: 'active',
        roleKeys: [],
        delegateForUserId: undefined,
        phone: '',
        roleTitle: '',
        ccsEligible: false,
      })
      setSelectedNPI(null)
      addUserNpiAvailability.clearDuplicateError()
      showSuccess('User created successfully.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to add user'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.add' } })
    } finally {
      isAddingUserRef.current = false
      setIsAddingUser(false)
    }
  }

  const updateStatus = async (userId: string, status: string) => {
    setError(null)
    try {
      const response = await fetch(`${apiBaseUrl}/auth/users/${userId}/status`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ status }),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to update status (${response.status})`)
      }
      showSuccess('Status updated successfully.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update status'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.status' } })
    }
  }

  const assignRole = async (userId: string, roleKey: string, delegateForUserId?: string) => {
    setError(null)
    try {
      const trimmed = roleKey.trim()
      if (!trimmed) {
        return
      }
      const response = await fetch(`${apiBaseUrl}/auth/users/${userId}/roles`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          roleKey: trimmed,
          delegateForUserId: delegateForUserId || undefined,
        }),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to assign role (${response.status})`)
      }
      showSuccess('Role added successfully.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to assign role'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.roles.assign' } })
    }
  }

  const linkPractitioner = async (userId: string, npi: string, name?: string) => {
    setError(null)
    try {
      const response = await fetch(`${apiBaseUrl}/auth/users/link-practitioner`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ userId, npi: npi.trim(), name: name?.trim() || undefined }),
      })
      if (!response.ok) {
        const text = await response.text()
        let message = text
        try {
          const data = JSON.parse(text) as { detail?: string }
          if (typeof data.detail === 'string') message = data.detail
        } catch {
          // use text as message
        }
        throw new Error(message)
      }
      showSuccess('Practitioner linked and clinician role assigned.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to link practitioner'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.link-practitioner' } })
    }
  }

  const removeRole = async (userId: string, roleKey: string) => {
    setError(null)
    try {
      const response = await fetch(`${apiBaseUrl}/auth/users/${userId}/roles/${encodeURIComponent(roleKey)}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to remove role (${response.status})`)
      }
      showSuccess('Role removed successfully.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to remove role'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.roles.remove' } })
    }
  }

  const purgeUser = async (userId: string) => {
    setError(null)
    try {
      const confirmed = window.confirm(
        'Remove user?\n\nThis will permanently delete the user from IMDB and delete the matching Auth0 account(s). This cannot be undone.'
      )
      if (!confirmed) return

      const response = await fetch(`${apiBaseUrl}/auth/users/${userId}/purge`, {
        method: 'DELETE',
        headers: await authHeaders(),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Failed to remove user (${response.status})`)
      }
      showSuccess('User removed successfully.')
      await loadMembers()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to remove user'
      setError(message)
      Sentry.captureException(e, { tags: { operation: 'admin.users.purge' } })
    }
  }

  /** PATCH display name, NPI, phone, and/or medical title for an org member (same body as PATCH /auth/me). */
  const updateUserProfile = async (
    userId: string,
    body: ProfileUpdateRequest
  ): Promise<AdminProfilePatchResult> => {
    setError(null)
    try {
      const response = await fetch(
        `${apiBaseUrl}/auth/users/${encodeURIComponent(userId)}/profile`,
        {
          method: 'PATCH',
          headers: await authHeaders(),
          body: JSON.stringify(body),
        }
      )
      if (!response.ok) {
        const message = await parseApiErrorMessage(response)
        const fullMessage =
          response.status === 502
            ? `${message} If this persists, try again or contact support — account data may be temporarily out of sync.`
            : message
        // Inline edit form shows result.message via setProfileError; avoid duplicating the page banner.
        Sentry.captureMessage('PATCH /auth/users/:id/profile failed', {
          level: 'warning',
          extra: { status: response.status, userId, body },
        })
        return { ok: false, message: fullMessage }
      }
      showSuccess('Profile updated successfully.')
      await loadMembers()
      await loadClinicians()
      return { ok: true }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update profile'
      Sentry.captureException(e, { tags: { operation: 'admin.users.profile.patch' } })
      return { ok: false, message }
    }
  }

  useEffect(() => {
    // Defense in depth: even though the route-level PermissionGuard prevents
    // non-admins from reaching this page, also gate mount-time admin API calls
    // on a confirmed `org_admin` role so a brief stale-state window cannot fire
    // 4× 403 fetches against /auth/users etc. (GEN-3078).
    if (authLoading) return
    if (!hasRole('org_admin')) return
    void loadRoles()
    void loadClinicians()
    void loadDelegations()
    void loadMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading])

  const sortedRoleOptions = useMemo(() => {
    const options = roleOptions.filter((r) => !CASE_ROLE_KEYS.has(r.roleKey))
    options.sort((a, b) => {
      const aLabel = (a.roleName || a.roleKey).toLowerCase()
      const bLabel = (b.roleName || b.roleKey).toLowerCase()
      return aLabel.localeCompare(bLabel)
    })
    return options
  }, [roleOptions])

  const availableInitialRoleOptions = useMemo(() => {
    const selected = new Set(addUserForm.roleKeys || [])
    return sortedRoleOptions.filter((r) => !selected.has(r.roleKey))
  }, [addUserForm.roleKeys, sortedRoleOptions])

  const handleNPISelect = (result: NPIResult) => {
    setSelectedNPI(result)
    // NPPES already gives the parts separately, so they map straight across
    // without guessing where the surname starts. Names come back ALL CAPS, so
    // normalize the casing (toProperCaseName) for a natural-looking prefill.
    // The credential (e.g. "MD, PhD") can contain characters the medical-title
    // allowlist rejects and may exceed the length cap, so sanitize it before
    // using it as roleTitle (otherwise the field is invalid and submit stays
    // disabled).
    setAddUserForm((prev) => ({
      ...prev,
      npiNumber: result.npiNumber,
      firstName: prev.firstName.trim() ? prev.firstName : toProperCaseName(result.firstName),
      middleName: prev.middleName.trim() ? prev.middleName : toProperCaseName(result.middleName ?? ''),
      lastName: prev.lastName.trim() ? prev.lastName : toProperCaseName(result.lastName),
      roleTitle: (prev.roleTitle ?? '').trim() ? prev.roleTitle : sanitizeRoleTitle(result.credential),
    }))
    setIsNPIModalOpen(false)
  }

  return (
    <div className="min-h-screen bg-background-secondary-medium dark:bg-gray-800">
      <Navbar
        userAvatar={user?.picture}
        userName={user?.name}
        onLogout={handleLogout}
        organizationName={getClientName(config)}
      />

      <div className="mx-auto max-w-[1800px] px-4 py-6">
        <OrgAdminGuard
          fallback={
            <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-sm">
              <h1 className="text-xl font-semibold text-text-heading dark:text-white">Admin</h1>
              <p className="mt-2 text-sm text-text-body dark:text-gray-400">You don’t have access to admin tools for this organization.</p>
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={() => navigate('/orders')}
                className="mt-6 focus-visible:ring-brand-600"
              >
                Back to Workspace
              </Button>
            </div>
          }
        >
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-text-heading dark:text-white">Admin</h1>
              <p className="text-sm text-text-body dark:text-gray-400">Manage organization users and roles.</p>
            </div>
            <button
              type="button"
              onClick={loadMembers}
              className="inline-flex items-center rounded-full border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800"
            >
              Refresh
            </button>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 px-4 py-2 text-sm text-text-fg-error-strong dark:text-error-100">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-base border border-border-success-subtle dark:border-success-700 bg-success-50 dark:bg-gray-900 px-4 py-2 text-sm text-text-fg-success-strong dark:text-success-100">
              {successMessage}
            </div>
          )}

          {/* 2-Column Layout */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start">
            {/* Left Column - Add User Form (takes 1/3) */}
            <div className="lg:sticky lg:top-4 lg:col-span-1">
              <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm">
                <h2 className="text-sm font-semibold text-text-heading dark:text-white">Add User</h2>
                <p className="mt-1 text-xs text-text-body dark:text-gray-400">
                  Creates the Auth0 account and database user. Welcome email is off by default; users sign in with
                  the usual passwordless flow.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
                  {/* Status and the names share a full-width sub-grid so last name wraps to the
                      left edge instead of indenting under first name. Keeping the widths the row
                      already used: a narrow status and three equal name fields. */}
                  <div className="grid grid-cols-1 gap-3 md:col-span-6 md:grid-cols-12">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-text-body dark:text-gray-400">Status</label>
                      <div className="relative mt-1">
                        <select
                          value={addUserForm.status || 'active'}
                          onChange={(e) => setAddUserForm((prev) => ({ ...prev, status: e.target.value }))}
                          className="w-full appearance-none rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-10 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                        >
                          <option value="active">active</option>
                          <option value="inactive">inactive</option>
                        </select>
                        <ChevronDown
                          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-body dark:text-gray-400"
                          aria-hidden
                        />
                      </div>
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium text-text-body dark:text-gray-400">First name</label>
                      <input
                        value={addUserForm.firstName}
                        onChange={(e) => setAddUserForm((prev) => ({ ...prev, firstName: e.target.value }))}
                        placeholder="Jane"
                        autoComplete="given-name"
                        maxLength={NAME_FIELD_MAX_LENGTH}
                        aria-invalid={Boolean(addUserFirstNameError)}
                        className="mt-1 w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      />
                      {addUserFirstNameError && <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{addUserFirstNameError}</p>}
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium text-text-body dark:text-gray-400">Middle name (optional)</label>
                      <input
                        value={addUserForm.middleName}
                        onChange={(e) => setAddUserForm((prev) => ({ ...prev, middleName: e.target.value }))}
                        placeholder="Ann"
                        autoComplete="additional-name"
                        maxLength={MIDDLE_NAME_FIELD_MAX_LENGTH}
                        aria-invalid={Boolean(addUserMiddleNameError)}
                        className="mt-1 w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      />
                      {addUserMiddleNameError && <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{addUserMiddleNameError}</p>}
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium text-text-body dark:text-gray-400">Last name</label>
                      <input
                        value={addUserForm.lastName}
                        onChange={(e) => setAddUserForm((prev) => ({ ...prev, lastName: e.target.value }))}
                        placeholder="Clinician"
                        autoComplete="family-name"
                        maxLength={NAME_FIELD_MAX_LENGTH}
                        aria-invalid={Boolean(addUserLastNameError)}
                        className="mt-1 w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      />
                      {addUserLastNameError && <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{addUserLastNameError}</p>}
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-text-body dark:text-gray-400">Medical title (optional)</label>
                    <input
                      value={addUserForm.roleTitle ?? ''}
                      onChange={(e) => setAddUserForm((prev) => ({ ...prev, roleTitle: e.target.value }))}
                      placeholder="MD"
                      maxLength={ROLE_TITLE_MAX_LENGTH}
                      aria-invalid={addUserRoleTitleInvalid}
                      className="mt-1 w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                    />
                    {addUserRoleTitleInvalid && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">
                        {!isWithinLength(addUserForm.roleTitle ?? '', ROLE_TITLE_MAX_LENGTH)
                          ? ROLE_TITLE_TOO_LONG_MESSAGE
                          : isValidFreeText(addUserForm.roleTitle ?? '')
                            ? ROLE_TITLE_INVALID_MESSAGE
                            : FREE_TEXT_INVALID_MESSAGE}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-text-body dark:text-gray-400">Email</label>
                    <input
                      value={addUserForm.email}
                      onChange={(e) => setAddUserForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="jane@hospital.org"
                      type="email"
                      maxLength={EMAIL_MAX_LENGTH}
                      aria-invalid={addUserEmailInvalid || addUserEmailDuplicate}
                      className={`mt-1 w-full rounded-base border bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                        addUserEmailInvalid || addUserEmailDuplicate
                          ? 'border-border-error dark:border-error-700 focus:border-border-error dark:focus:border-error-700 focus:ring-error-500'
                          : 'border-border-base-medium dark:border-gray-700 focus:border-brand-600 dark:focus:border-brand-500 focus:ring-brand-600'
                      }`}
                    />
                    {addUserEmailInvalid && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">Please enter a valid email address.</p>
                    )}
                    {!addUserEmailInvalid && addUserEmailDuplicate && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">
                        A user with this email already exists in the organization.
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-text-body dark:text-gray-400">Phone (optional)</label>
                    <input
                      value={addUserForm.phone ?? ''}
                      onChange={(e) => setAddUserForm((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      maxLength={PHONE_MAX_LENGTH}
                      aria-invalid={addUserPhoneInvalid}
                      className="mt-1 w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                    />
                    {addUserPhoneInvalid && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">
                        {!isWithinLength(addUserForm.phone ?? '', PHONE_MAX_LENGTH)
                          ? PHONE_TOO_LONG_MESSAGE
                          : !isValidFreeText(addUserForm.phone ?? '')
                            ? FREE_TEXT_INVALID_MESSAGE
                            : PHONE_INVALID_MESSAGE}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-text-body dark:text-gray-400">NPI (optional)</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        value={addUserForm.npiNumber}
                        onChange={(e) => {
                          const next = e.target.value
                          setAddUserForm((prev) => ({ ...prev, npiNumber: next }))
                          // Drop the green confirmation card once the field no longer
                          // matches the looked-up provider, so it can't go stale.
                          if (selectedNPI && next.trim() !== selectedNPI.npiNumber) {
                            setSelectedNPI(null)
                          }
                        }}
                        onBlur={addUserNpiAvailability.validateOnBlur}
                        placeholder="1234567890"
                        maxLength={12}
                        inputMode="numeric"
                        aria-invalid={addUserNpiInvalid || addUserNpiAvailability.hasDuplicate}
                        className={`w-[16ch] shrink-0 rounded-base border bg-white dark:bg-gray-900 px-2 py-2 font-mono text-sm focus:outline-none focus:ring-1 ${
                          addUserNpiInvalid || addUserNpiAvailability.hasDuplicate
                            ? 'border-border-error dark:border-error-700 focus:border-border-error dark:focus:border-error-700 focus:ring-error-500'
                            : 'border-border-base-medium dark:border-gray-700 focus:border-brand-600 dark:focus:border-brand-500 focus:ring-brand-600'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setIsNPIModalOpen(true)}
                        className="whitespace-nowrap rounded-full border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                      >
                        Lookup
                      </button>
                    </div>
                    {selectedNPI && (
                      <div className="mt-2 rounded-base border border-border-success-subtle dark:border-success-700 bg-success-50 dark:bg-gray-900 px-3 py-2 text-xs text-text-fg-success-strong dark:text-success-100">
                        <div className="font-medium">
                          {selectedNPI.firstName} {selectedNPI.lastName}
                        </div>
                        <div className="font-mono">{selectedNPI.npiNumber}</div>
                      </div>
                    )}
                    {addUserNpiInvalid && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{NPI_INVALID_MESSAGE}</p>
                    )}
                    {addUserNpiAvailability.isChecking && (
                      <p className="mt-1 text-xs text-text-body dark:text-gray-400">Checking NPI availability…</p>
                    )}
                    {addUserNpiAvailability.duplicateError && (
                      <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{addUserNpiAvailability.duplicateError}</p>
                    )}
                  </div>
                  {ccsEnabled && (
                    <div className="md:col-span-6">
                      <label className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(addUserForm.ccsEligible)}
                          onChange={(e) =>
                            setAddUserForm((prev) => ({ ...prev, ccsEligible: e.target.checked }))
                          }
                          disabled={!addUserForm.npiNumber.trim()}
                          className="mt-0.5 disabled:cursor-not-allowed"
                        />
                        <span className="text-xs text-text-body dark:text-gray-400">
                          <span className="font-medium">CCS eligible</span>
                          <span className="block text-[10px] text-text-body dark:text-gray-400">
                            California Children&rsquo;s Services. Applies to the linked practitioner; requires an
                            NPI.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-medium text-text-body dark:text-gray-400">Initial roles (optional)</label>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {(addUserForm.roleKeys || []).map((roleKey) => (
                      <span
                        key={roleKey}
                        className="inline-flex items-center gap-2 rounded-full border border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-3 py-1 text-xs font-medium text-text-body dark:text-gray-400"
                      >
                        {roleKey}
                        <button
                          type="button"
                          onClick={() => {
                            setAddUserForm((prev) => ({
                              ...prev,
                              roleKeys: (prev.roleKeys || []).filter((r) => r !== roleKey),
                            }))
                            // Reset clinician selection if delegate is removed
                            if (roleKey === 'delegate') {
                              setAddUserForm((prev) => ({ ...prev, delegateForUserId: undefined }))
                            }
                          }}
                          className="rounded-full px-1 text-text-body dark:text-gray-400 hover:bg-background-quaternary dark:hover:bg-gray-800 hover:text-text-heading dark:hover:text-white"
                          aria-label={`Remove role ${roleKey}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}

                    <details
                      ref={addRolesDetailsRef}
                      className="relative"
                      open={addRolesMenuOpen}
                      onToggle={(e) => setAddRolesMenuOpen(e.currentTarget.open)}
                    >
                      <summary className="cursor-pointer select-none list-none rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800">
                        Add roles
                      </summary>
                      <div className="absolute z-10 mt-2 w-72 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-3 shadow-lg">
                        {availableInitialRoleOptions.length === 0 ? (
                          <div className="text-sm text-text-body dark:text-gray-400">No additional roles available.</div>
                        ) : (
                          <div className="max-h-56 overflow-auto pr-1">
                            {availableInitialRoleOptions.map((r) => {
                              const label = r.roleName ? `${r.roleName} (${r.roleKey})` : r.roleKey
                              return (
                                <button
                                  key={r.roleKey}
                                  type="button"
                                  onClick={() => {
                                    setAddUserForm((prev) => ({
                                      ...prev,
                                      roleKeys: [...(prev.roleKeys || []), r.roleKey],
                                    }))
                                    setAddRolesMenuOpen(false)
                                  }}
                                  className="flex w-full items-start gap-2 rounded-base px-2 py-1 text-left hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                                >
                                  <span className="text-sm text-text-heading dark:text-white">
                                    {label}
                                    {r.roleDescription && (
                                      <div className="mt-0.5 text-xs text-text-body dark:text-gray-400">{r.roleDescription}</div>
                                    )}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </details>
                  </div>

                  {(addUserForm.roleKeys || []).includes('delegate') && (
                    <div className="mt-3">
                      <label className="mb-2 block text-xs font-medium text-text-body dark:text-gray-400">
                        Select Clinician to delegate for:
                      </label>
                      <div className="relative">
                        <select
                          value={addUserForm.delegateForUserId || ''}
                          onChange={(e) =>
                            setAddUserForm((prev) => ({ ...prev, delegateForUserId: e.target.value || undefined }))
                          }
                          className="w-full appearance-none rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-10 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                        >
                          <option value="">-- Select Clinician --</option>
                          {clinicians.map((c) => (
                            <option key={c.userId} value={c.userId}>
                              {clinicianOptionLabel(c, ccsEnabled)}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-body dark:text-gray-400"
                          aria-hidden
                        />
                      </div>
                      {!addUserForm.delegateForUserId && (
                        <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">Required for delegate role</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="md"
                    type="button"
                    onClick={addUser}
                    disabled={!membersLoaded || isAddingUser || addUserNameInvalid || addUserEmailInvalid || addUserEmailDuplicate || addUserPhoneInvalid || addUserRoleTitleInvalid || addUserNpiInvalid || addUserNpiAvailability.hasDuplicate}
                    className="focus-visible:ring-brand-600"
                  >
                    {isAddingUser ? 'Adding…' : 'Add user'}
                  </Button>
                  {!membersLoaded && isLoading && (
                    <p className="mt-1 text-xs text-text-body dark:text-gray-400">Loading existing users…</p>
                  )}
                  {!membersLoaded && !isLoading && (
                    <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">
                      Couldn’t load existing users, so new users can’t be added yet.{' '}
                      <button
                        type="button"
                        onClick={loadMembers}
                        className="font-medium text-text-fg-brand dark:text-brand-400 underline hover:text-brand-800 dark:hover:text-brand-300"
                      >
                        Retry
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Users Table (takes 2/3) */}
            <div className="flex min-h-[calc(100vh-200px)] flex-col lg:col-span-2">
              <div className="flex flex-1 flex-col rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                <div className="border-b border-border-base dark:border-gray-700 px-4 py-3">
                  <h2 className="text-sm font-semibold text-text-heading dark:text-white">Users</h2>
                </div>
                <div className="flex-1 overflow-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800">
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-body dark:text-gray-400">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-body dark:text-gray-400">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-body dark:text-gray-400">Roles</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-text-body dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-text-body dark:text-gray-400">
                            Loading…
                          </td>
                        </tr>
                      ) : members.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-text-body dark:text-gray-400">
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        members.map((m) => (
                          <AdminUserRow
                            key={m.membershipId}
                            member={m}
                            roleOptions={sortedRoleOptions}
                            clinicians={clinicians}
                            delegations={delegations}
                            currentUserId={currentUserId}
                            ccsEnabled={ccsEnabled}
                            onUpdateStatus={updateStatus}
                            onAssignRole={assignRole}
                            onLinkPractitioner={linkPractitioner}
                            onRemoveRole={removeRole}
                            onPurgeUser={purgeUser}
                            onUpdateProfile={updateUserProfile}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </OrgAdminGuard>
      </div>

      <NPILookupModal
        isOpen={isNPIModalOpen}
        onClose={() => setIsNPIModalOpen(false)}
        onSelect={handleNPISelect}
        initialQuery={addUserForm.npiNumber}
      />
    </div>
  )
}

function AdminUserRow({
  member,
  roleOptions,
  clinicians,
  delegations,
  currentUserId,
  ccsEnabled,
  onUpdateStatus,
  onAssignRole,
  onLinkPractitioner,
  onRemoveRole,
  onPurgeUser,
  onUpdateProfile,
}: {
  member: OrgMember
  roleOptions: RoleOption[]
  clinicians: Clinician[]
  delegations: Delegation[]
  currentUserId: string | null
  ccsEnabled: boolean
  onUpdateStatus: (userId: string, status: string) => Promise<void>
  onAssignRole: (userId: string, roleKey: string, delegateForUserId?: string) => Promise<void>
  onLinkPractitioner: (userId: string, npi: string, name?: string) => Promise<void>
  onRemoveRole: (userId: string, roleKey: string) => Promise<void>
  onPurgeUser: (userId: string) => Promise<void>
  onUpdateProfile: (userId: string, body: ProfileUpdateRequest) => Promise<AdminProfilePatchResult>
}) {
  const [pendingRoleKeys, setPendingRoleKeys] = useState<string[]>([])
  const [selectedClinicianId, setSelectedClinicianId] = useState<string>('')
  const [linkPractitionerNpi, setLinkPractitionerNpi] = useState('')
  const [linkPractitionerName, setLinkPractitionerName] = useState('')

  const [rowRolesMenuOpen, setRowRolesMenuOpen] = useState(false)
  const rowRolesDetailsRef = useRef<HTMLDetailsElement>(null)

  useEffect(() => {
    if (!rowRolesMenuOpen) return
    const onPointerDown = (e: PointerEvent) => {
      const root = rowRolesDetailsRef.current
      if (root && !root.contains(e.target as Node)) {
        setRowRolesMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [rowRolesMenuOpen])

  const [profileEditing, setProfileEditing] = useState(false)
  const [editProfileFirstName, setEditProfileFirstName] = useState('')
  const [editProfileMiddleName, setEditProfileMiddleName] = useState('')
  const [editProfileLastName, setEditProfileLastName] = useState('')
  const [editProfileNpi, setEditProfileNpi] = useState('')
  const [editProfilePhone, setEditProfilePhone] = useState('')
  const [editProfileRoleTitle, setEditProfileRoleTitle] = useState('')
  const [editProfileCcsEligible, setEditProfileCcsEligible] = useState(false)
  const [profileSaveLoading, setProfileSaveLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  // Find delegation where this user is the grantee (delegate)
  const userDelegation = delegations.find((d) => d.granteeUserId === member.userId)

  // Filter clinicians to exclude the current member (can't delegate to yourself)
  const availableClinicians = useMemo(() => {
    return clinicians.filter((c) => c.userId !== member.userId)
  }, [clinicians, member.userId])

  const availableRoleOptions = useMemo(() => {
    const current = new Set(member.roles)
    return roleOptions.filter((r) => !current.has(r.roleKey))
  }, [member.roles, roleOptions])

  const hasDelegateRole = pendingRoleKeys.includes('delegate')
  const hasClinicianRole = pendingRoleKeys.includes('clinician')
  const needsClinicianSelection = hasDelegateRole && !selectedClinicianId
  const needsNpiForClinician = hasClinicianRole && !linkPractitionerNpi.trim()
  // Trim to match the submit-time checks below so a whitespace-only NPI isn't treated as invalid.
  const linkPractitionerNpiInvalid = !isValidNpi(linkPractitionerNpi.trim())

  const addSelectedRoles = async () => {
    if (pendingRoleKeys.length === 0) return

    if (hasDelegateRole && !selectedClinicianId) {
      alert('Please select a clinician for the delegate role.')
      return
    }

    if (hasClinicianRole && !linkPractitionerNpi.trim()) {
      alert('NPI is required to add the clinician role (creates and links a practitioner).')
      return
    }

    if (hasClinicianRole && !isValidNpi(linkPractitionerNpi.trim())) {
      alert(NPI_INVALID_MESSAGE)
      return
    }

    if (hasClinicianRole) {
      const npiCheck = await linkPractitionerNpiAvailability.validateBeforeSubmit()
      if (!npiCheck.ok) {
        alert(npiCheck.message)
        return
      }
    }

    for (const roleKey of pendingRoleKeys) {
      if (roleKey === 'clinician') {
        await onLinkPractitioner(member.userId, linkPractitionerNpi.trim(), linkPractitionerName.trim() || undefined)
      } else {
        await onAssignRole(member.userId, roleKey, roleKey === 'delegate' ? selectedClinicianId : undefined)
      }
    }
    setPendingRoleKeys([])
    setSelectedClinicianId('')
    setLinkPractitionerNpi('')
    setLinkPractitionerName('')
  }

  const npiLine = orgMemberNpiDisplay(member)
  const baselineFirstName = (member.firstName ?? '').trim()
  const baselineMiddleName = (member.middleName ?? '').trim()
  const baselineLastName = (member.lastName ?? '').trim()
  const baselineNpi = orgMemberNpiDisplay(member) ?? ''
  const baselinePhone = orgMemberPhone(member)
  const baselineRoleTitle = orgMemberRoleTitle(member)
  // CCS eligibility baseline. `null`/`undefined` (no linked practitioner or
  // org not CCS-configured) is treated as false for the checkbox; the toggle is
  // only shown when the org is CCS-enabled.
  const baselineCcsEligible = member.ccsEligible === true

  const profileNpiAvailability = useOrgNpiAvailability({
    npi: editProfileNpi,
    excludeUserId: member.userId,
    baselineNpi,
    enabled: profileEditing,
  })

  const linkPractitionerNpiAvailability = useOrgNpiAvailability({
    npi: linkPractitionerNpi,
    excludeUserId: member.userId,
    baselineNpi: '',
    enabled: hasClinicianRole,
  })

  const profileFirstNameChanged = editProfileFirstName.trim() !== baselineFirstName
  const profileMiddleNameChanged = editProfileMiddleName.trim() !== baselineMiddleName
  const profileLastNameChanged = editProfileLastName.trim() !== baselineLastName
  const profileNameChanged = profileFirstNameChanged || profileMiddleNameChanged || profileLastNameChanged

  const profileDirty =
    profileNameChanged ||
    editProfileNpi.trim() !== baselineNpi ||
    editProfilePhone.trim() !== baselinePhone ||
    editProfileRoleTitle.trim() !== baselineRoleTitle ||
    (ccsEnabled && editProfileCcsEligible !== baselineCcsEligible)

  // Length is only enforced on fields the admin changed (mirrors isValidUserNameIfChanged),
  // so a legacy over-length value doesn't block an NPI-only / phone-only profile edit.
  const profileFirstNameError = profileFirstNameChanged
    ? (userNameErrorIfChanged(editProfileFirstName, baselineFirstName) ??
      nameFieldLengthError(editProfileFirstName))
    : undefined
  const profileMiddleNameError = profileMiddleNameChanged
    ? (userNameErrorIfChanged(editProfileMiddleName, baselineMiddleName) ??
      middleNameFieldLengthError(editProfileMiddleName))
    : undefined
  const profileLastNameError = profileLastNameChanged
    ? (userNameErrorIfChanged(editProfileLastName, baselineLastName) ??
      nameFieldLengthError(editProfileLastName))
    : undefined
  const profileNameInvalid = Boolean(profileFirstNameError || profileMiddleNameError || profileLastNameError)
  // Trim to match `profilePatchBody` (which trims NPI) so a whitespace-only value isn't flagged invalid.
  const profileNpiInvalid = !isValidNpi(editProfileNpi.trim())
  const profilePhoneInvalid =
    !isValidFreeText(editProfilePhone) ||
    !isWithinLength(editProfilePhone, PHONE_MAX_LENGTH) ||
    !isValidPhone(editProfilePhone)
  const profileRoleTitleInvalid =
    !isValidFreeText(editProfileRoleTitle) ||
    !isValidRoleTitle(editProfileRoleTitle) ||
    !isWithinLength(editProfileRoleTitle, ROLE_TITLE_MAX_LENGTH)
  const profileFieldsValid =
    !profileNameInvalid && !profileNpiInvalid && !profilePhoneInvalid && !profileRoleTitleInvalid

  const profilePatchBody = useMemo((): ProfileUpdateRequest | null => {
    const nextFirstName = editProfileFirstName.trim()
    const nextMiddleName = editProfileMiddleName.trim()
    const nextLastName = editProfileLastName.trim()
    const nextNpi = editProfileNpi.trim()
    const nextPhone = editProfilePhone.trim()
    const nextRoleTitle = editProfileRoleTitle.trim()
    const body: ProfileUpdateRequest = {}
    if (nextFirstName !== baselineFirstName) body.firstName = nextFirstName
    if (nextMiddleName !== baselineMiddleName) body.middleName = nextMiddleName
    if (nextLastName !== baselineLastName) body.lastName = nextLastName
    if (nextNpi !== baselineNpi) body.npi = nextNpi
    if (nextPhone !== baselinePhone) body.phone = nextPhone
    if (nextRoleTitle !== baselineRoleTitle) body.roleTitle = nextRoleTitle
    // Tri-state CCS eligibility: only send when the org is CCS-enabled AND the
    // admin actually toggled it, so an unrelated edit never clears eligibility.
    // CCS lives on the linked practitioner, so it's only meaningful with a
    // non-empty NPI. If the NPI has been cleared (the checkbox is then disabled
    // but retains its prior value), omit ccsEligible so we never send it for a
    // practitioner-less profile, which the feature treats as invalid.
    const ccsChanged = ccsEnabled && editProfileCcsEligible !== baselineCcsEligible
    if (ccsChanged && nextNpi) body.ccsEligible = editProfileCcsEligible
    // A ccsEligible-only update is valid, so a body with just `ccsEligible` is
    // allowed. Only bail when nothing changed at all.
    if (Object.keys(body).length === 0) return null
    // Whether any name/npi/phone/roleTitle field changed (i.e. this is not a
    // CCS-only update). CCS-only updates skip the all-empty guard below, which
    // is only relevant to the text fields.
    const textFieldsChanged =
      body.firstName !== undefined ||
      body.middleName !== undefined ||
      body.lastName !== undefined ||
      body.npi !== undefined ||
      body.phone !== undefined ||
      body.roleTitle !== undefined
    if (textFieldsChanged) {
      // Name parts are sent independently, so a contact-only edit no longer has to
      // re-send the name to keep the practitioner record consistent — the server
      // leaves omitted parts alone.
      const effectiveName = composeDisplayName(
        body.firstName ?? baselineFirstName,
        body.middleName ?? baselineMiddleName,
        body.lastName ?? baselineLastName
      )
      const effectiveNpi = body.npi !== undefined ? body.npi.trim() : baselineNpi
      const effectivePhone = body.phone !== undefined ? body.phone.trim() : baselinePhone
      const effectiveRoleTitle = body.roleTitle !== undefined ? body.roleTitle.trim() : baselineRoleTitle
      if (!effectiveName && !effectiveNpi && !effectivePhone && !effectiveRoleTitle && body.ccsEligible === undefined)
        return null
    }
    return body
  }, [
    editProfileFirstName,
    editProfileMiddleName,
    editProfileLastName,
    editProfileNpi,
    editProfilePhone,
    editProfileRoleTitle,
    editProfileCcsEligible,
    ccsEnabled,
    baselineFirstName,
    baselineMiddleName,
    baselineLastName,
    baselineNpi,
    baselinePhone,
    baselineRoleTitle,
    baselineCcsEligible,
  ])

  const startProfileEdit = () => {
    setProfileError(null)
    setEditProfileFirstName(baselineFirstName)
    setEditProfileMiddleName(baselineMiddleName)
    setEditProfileLastName(baselineLastName)
    setEditProfileNpi(orgMemberNpiDisplay(member) ?? '')
    setEditProfilePhone(orgMemberPhone(member))
    setEditProfileRoleTitle(orgMemberRoleTitle(member))
    setEditProfileCcsEligible(member.ccsEligible === true)
    setProfileEditing(true)
  }

  const cancelProfileEdit = () => {
    setProfileEditing(false)
    setProfileError(null)
  }

  const saveProfile = async () => {
    setProfileError(null)
    if (!profileFieldsValid) {
      if (profileNameInvalid) {
        setProfileError(
          profileFirstNameError ?? profileMiddleNameError ?? profileLastNameError ?? USER_NAME_INVALID_MESSAGE
        )
      } else if (profileNpiInvalid) {
        setProfileError(NPI_INVALID_MESSAGE)
      } else if (profilePhoneInvalid) {
        setProfileError(
          !isWithinLength(editProfilePhone, PHONE_MAX_LENGTH)
            ? PHONE_TOO_LONG_MESSAGE
            : !isValidFreeText(editProfilePhone)
              ? FREE_TEXT_INVALID_MESSAGE
              : PHONE_INVALID_MESSAGE
        )
      } else if (profileRoleTitleInvalid) {
        setProfileError(
          !isWithinLength(editProfileRoleTitle, ROLE_TITLE_MAX_LENGTH)
            ? ROLE_TITLE_TOO_LONG_MESSAGE
            : isValidFreeText(editProfileRoleTitle)
              ? ROLE_TITLE_INVALID_MESSAGE
              : FREE_TEXT_INVALID_MESSAGE
        )
      } else {
        setProfileError(FREE_TEXT_INVALID_MESSAGE)
      }
      return
    }
    const body = profilePatchBody
    if (!body) {
      setProfileError(profileDirty ? 'Provide a name, NPI, phone, and/or medical title.' : 'Nothing to save.')
      return
    }
    const effectiveFirstName = body.firstName ?? baselineFirstName
    const effectiveLastName = body.lastName ?? baselineLastName
    if (profileNameChanged && (!effectiveFirstName.trim() || !effectiveLastName.trim())) {
      setProfileError('First and last name are both required.')
      return
    }
    const effectiveName = composeDisplayName(
      effectiveFirstName,
      body.middleName ?? baselineMiddleName,
      effectiveLastName
    )
    const effectiveNpi = body.npi !== undefined ? body.npi.trim() : baselineNpi
    const effectivePhone = body.phone !== undefined ? body.phone.trim() : baselinePhone
    const effectiveRoleTitle = body.roleTitle !== undefined ? body.roleTitle.trim() : baselineRoleTitle
    if (
      !effectiveName &&
      !effectiveNpi &&
      !effectivePhone &&
      !effectiveRoleTitle &&
      body.ccsEligible === undefined
    ) {
      setProfileError('Provide a name, NPI, phone, and/or medical title.')
      return
    }
    if (body.npi !== undefined) {
      const npiCheck = await profileNpiAvailability.validateBeforeSubmit()
      if (!npiCheck.ok) {
        setProfileError(npiCheck.message)
        return
      }
    }
    setProfileSaveLoading(true)
    try {
      const result = await onUpdateProfile(member.userId, body)
      if (result.ok) {
        setProfileEditing(false)
        setProfileError(null)
      } else {
        setProfileError(result.message)
      }
    } finally {
      setProfileSaveLoading(false)
    }
  }

  // First and last are both required, so a save that would blank either is blocked.
  const profilePatchHasEmptyName =
    profilePatchBody !== null &&
    profileNameChanged &&
    (!(profilePatchBody.firstName ?? baselineFirstName).trim() ||
      !(profilePatchBody.lastName ?? baselineLastName).trim())

  const canSaveProfile =
    Boolean(profilePatchBody) &&
    profileFieldsValid &&
    !profileSaveLoading &&
    !profilePatchHasEmptyName &&
    !profileNpiAvailability.hasDuplicate &&
    !profileNpiAvailability.isChecking

  return (
    <tr className="border-b border-border-base dark:border-gray-700">
      <td className="px-3 py-2.5 align-top">
        {profileEditing ? (
          <div className="max-w-[220px] space-y-2">
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">First name</label>
              <input
                type="text"
                value={editProfileFirstName}
                onChange={(e) => setEditProfileFirstName(e.target.value)}
                disabled={profileSaveLoading}
                maxLength={NAME_FIELD_MAX_LENGTH}
                aria-invalid={Boolean(profileFirstNameError)}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profileFirstNameError && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">{profileFirstNameError}</p>
              )}
            </div>
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">
                Middle name (optional)
              </label>
              <input
                type="text"
                value={editProfileMiddleName}
                onChange={(e) => setEditProfileMiddleName(e.target.value)}
                disabled={profileSaveLoading}
                maxLength={MIDDLE_NAME_FIELD_MAX_LENGTH}
                aria-invalid={Boolean(profileMiddleNameError)}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profileMiddleNameError && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">{profileMiddleNameError}</p>
              )}
            </div>
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">Last name</label>
              <input
                type="text"
                value={editProfileLastName}
                onChange={(e) => setEditProfileLastName(e.target.value)}
                disabled={profileSaveLoading}
                maxLength={NAME_FIELD_MAX_LENGTH}
                aria-invalid={Boolean(profileLastNameError)}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profileLastNameError && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">{profileLastNameError}</p>
              )}
            </div>
            {member.userEmail && (
              <div>
                <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">Login email</label>
                <input
                  type="email"
                  readOnly
                  value={member.userEmail}
                  className="w-full cursor-default rounded border border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-2 py-1 text-xs text-text-body dark:text-gray-400 focus:outline-none"
                  aria-readonly="true"
                />
              </div>
            )}
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">NPI</label>
              <input
                type="text"
                value={editProfileNpi}
                onChange={(e) => setEditProfileNpi(e.target.value)}
                onBlur={profileNpiAvailability.validateOnBlur}
                disabled={profileSaveLoading}
                maxLength={12}
                inputMode="numeric"
                aria-invalid={profileNpiInvalid || profileNpiAvailability.hasDuplicate}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 font-mono text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profileNpiInvalid && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">{NPI_INVALID_MESSAGE}</p>
              )}
              {profileNpiAvailability.isChecking && (
                <p className="mt-0.5 text-[10px] text-text-body dark:text-gray-400">Checking NPI availability…</p>
              )}
              {profileNpiAvailability.duplicateError && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">{profileNpiAvailability.duplicateError}</p>
              )}
            </div>
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">Phone</label>
              <input
                type="tel"
                value={editProfilePhone}
                onChange={(e) => setEditProfilePhone(e.target.value)}
                disabled={profileSaveLoading}
                maxLength={PHONE_MAX_LENGTH}
                aria-invalid={profilePhoneInvalid}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profilePhoneInvalid && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">
                  {!isWithinLength(editProfilePhone, PHONE_MAX_LENGTH)
                    ? PHONE_TOO_LONG_MESSAGE
                    : !isValidFreeText(editProfilePhone)
                      ? FREE_TEXT_INVALID_MESSAGE
                      : PHONE_INVALID_MESSAGE}
                </p>
              )}
            </div>
            <div>
              <label className="mb-0.5 block text-[10px] font-medium text-text-body dark:text-gray-400">Medical title</label>
              <input
                type="text"
                value={editProfileRoleTitle}
                onChange={(e) => setEditProfileRoleTitle(e.target.value)}
                disabled={profileSaveLoading}
                maxLength={ROLE_TITLE_MAX_LENGTH}
                placeholder="MD"
                aria-invalid={profileRoleTitleInvalid}
                className="w-full rounded border border-border-base-medium dark:border-gray-700 px-2 py-1 text-xs placeholder:text-text-subtle dark:placeholder:text-gray-500 focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:bg-background-disabled dark:disabled:bg-gray-800"
                autoComplete="off"
              />
              {profileRoleTitleInvalid && (
                <p className="mt-0.5 text-[10px] text-text-fg-error dark:text-error-100">
                  {!isWithinLength(editProfileRoleTitle, ROLE_TITLE_MAX_LENGTH)
                    ? ROLE_TITLE_TOO_LONG_MESSAGE
                    : isValidFreeText(editProfileRoleTitle)
                      ? ROLE_TITLE_INVALID_MESSAGE
                      : FREE_TEXT_INVALID_MESSAGE}
                </p>
              )}
            </div>
            {ccsEnabled && (
              <div>
                <label className="flex items-start gap-1.5">
                  <input
                    type="checkbox"
                    checked={editProfileCcsEligible}
                    onChange={(e) => setEditProfileCcsEligible(e.target.checked)}
                    disabled={profileSaveLoading || !editProfileNpi.trim()}
                    className="mt-0.5 disabled:cursor-not-allowed"
                  />
                  <span className="text-[10px] text-text-body dark:text-gray-400">
                    <span className="font-medium">CCS eligible</span>
                    <span className="block text-[9px] text-text-body dark:text-gray-400">
                      California Children&rsquo;s Services (linked practitioner; requires NPI)
                    </span>
                  </span>
                </label>
              </div>
            )}
            {profileError && <p className="text-[10px] text-text-fg-error dark:text-error-100">{profileError}</p>}
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => void saveProfile()}
                disabled={!canSaveProfile}
                className="focus-visible:ring-brand-600"
              >
                {profileSaveLoading ? 'Saving…' : 'Save'}
              </Button>
              <button
                type="button"
                onClick={cancelProfileEdit}
                disabled={profileSaveLoading}
                className="inline-flex items-center rounded border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-[11px] font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 text-sm font-medium text-text-heading dark:text-white">
                {member.userName || member.userEmail || member.userId}
              </span>
              <button
                type="button"
                onClick={startProfileEdit}
                className="shrink-0 rounded p-0.5 text-text-body dark:text-gray-400 hover:bg-background-tertiary dark:hover:bg-gray-800 hover:text-text-fg-brand dark:hover:text-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                aria-label="Edit user"
                title="Edit user"
              >
                <Pen className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {member.userEmail && member.userName && (
              <div className="mt-0.5 text-xs text-text-body dark:text-gray-400">{member.userEmail}</div>
            )}
            {npiLine && (
              <div className="mt-0.5 font-mono text-[10px] text-text-body dark:text-gray-400">NPI {npiLine}</div>
            )}
            {baselinePhone && (
              <div className="mt-0.5 text-[10px] text-text-body dark:text-gray-400">Phone {baselinePhone}</div>
            )}
            {baselineRoleTitle && (
              <div className="mt-0.5 text-[10px] text-text-body dark:text-gray-400">Title {baselineRoleTitle}</div>
            )}
            {ccsEnabled && member.ccsEligible != null && (
              <div className="mt-0.5 text-[10px] text-text-body dark:text-gray-400">
                CCS{' '}
                <span className={member.ccsEligible ? 'font-medium text-text-fg-success dark:text-success-100' : 'text-text-body dark:text-gray-400'}>
                  {member.ccsEligible ? 'eligible' : 'not eligible'}
                </span>
              </div>
            )}
            {(member.userName || member.userEmail) && (
              <div className="mt-0.5 font-mono text-[10px] text-text-body dark:text-gray-400">{member.userId}</div>
            )}
          </>
        )}
      </td>
      <td className="px-3 py-2.5">
        <div className="relative inline-block w-32">
          <select
            value={member.status === 'suspended' ? 'inactive' : member.status}
            onChange={(e) => onUpdateStatus(member.userId, e.target.value)}
            disabled={currentUserId === member.userId}
            className="w-full appearance-none rounded border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 pl-2 pr-8 py-1 text-xs focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:cursor-not-allowed disabled:bg-background-disabled dark:disabled:bg-gray-800 disabled:text-text-disabled dark:disabled:text-gray-600"
            title={currentUserId === member.userId ? 'You cannot change your own status' : undefined}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-body dark:text-gray-400"
            aria-hidden
          />
        </div>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {member.roles.map((role) => {
            const isCurrentUser = currentUserId === member.userId
            const isOrgAdminRole = role === 'org_admin'
            const cannotRemove = isCurrentUser && isOrgAdminRole
            const isDelegate = role === 'delegate'
            const clinicianName =
              isDelegate && userDelegation
                ? userDelegation.grantorName || userDelegation.grantorEmail || 'Unknown'
                : null

            return (
              <span
                key={role}
                className="inline-flex flex-col gap-0.5 rounded-full border border-border-base dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-text-body dark:text-gray-400"
              >
                <span className="flex items-center gap-1.5">
                  {role}
                  <button
                    type="button"
                    onClick={() => onRemoveRole(member.userId, role)}
                    disabled={cannotRemove}
                    className="rounded-full px-0.5 text-text-body dark:text-gray-400 hover:bg-background-quaternary dark:hover:bg-gray-800 hover:text-text-heading dark:hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label={cannotRemove ? `Cannot remove your own ${role} role` : `Remove role ${role}`}
                    title={cannotRemove ? 'You cannot remove your own org_admin role' : undefined}
                  >
                    ×
                  </button>
                </span>
                {clinicianName && <span className="text-[9px] italic text-text-body dark:text-gray-400">for {clinicianName}</span>}
              </span>
            )
          })}
          <details
            ref={rowRolesDetailsRef}
            className="relative"
            open={rowRolesMenuOpen}
            onToggle={(e) => setRowRolesMenuOpen(e.currentTarget.open)}
          >
            <summary className="cursor-pointer select-none list-none rounded border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800">
              + Add
            </summary>
            <div className="absolute z-10 mt-2 w-72 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-3 shadow-lg">
              {availableRoleOptions.length === 0 ? (
                <div className="text-sm text-text-body dark:text-gray-400">No additional roles available.</div>
              ) : (
                <>
                  <div className="max-h-56 overflow-auto pr-1">
                    {availableRoleOptions.map((r) => {
                      const checked = pendingRoleKeys.includes(r.roleKey)
                      const label = r.roleName ? `${r.roleName} (${r.roleKey})` : r.roleKey
                      return (
                        <label
                          key={r.roleKey}
                          className="flex cursor-pointer items-start gap-2 rounded-base px-2 py-1 hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const nextChecked = e.target.checked
                              setPendingRoleKeys((prev) => {
                                if (nextChecked) return [...prev, r.roleKey]
                                return prev.filter((x) => x !== r.roleKey)
                              })
                              // Reset clinician selection if delegate is unchecked
                              if (!nextChecked && r.roleKey === 'delegate') {
                                setSelectedClinicianId('')
                              }
                              if (nextChecked && r.roleKey === 'clinician') {
                                const existingNpi = orgMemberNpiDisplay(member)
                                if (existingNpi) {
                                  setLinkPractitionerNpi((prev) => (prev.trim() ? prev : existingNpi))
                                }
                              }
                              if (!nextChecked && r.roleKey === 'clinician') {
                                setLinkPractitionerNpi('')
                                setLinkPractitionerName('')
                              }
                            }}
                            className="mt-1"
                          />
                          <span className="text-sm text-text-heading dark:text-white">
                            {label}
                            {r.roleDescription && (
                              <div className="mt-0.5 text-xs text-text-body dark:text-gray-400">{r.roleDescription}</div>
                            )}
                          </span>
                        </label>
                      )
                    })}
                  </div>

                  {hasClinicianRole && (
                    <div className="mt-3 border-t border-border-base dark:border-gray-700 pt-3">
                      <p className="mb-2 text-xs text-text-body dark:text-gray-400">
                        Clinician role requires linking a practitioner (creates/links by NPI).
                      </p>
                      <label className="mb-1 block text-xs font-medium text-text-body dark:text-gray-400">NPI *</label>
                      <input
                        type="text"
                        value={linkPractitionerNpi}
                        onChange={(e) => setLinkPractitionerNpi(e.target.value)}
                        onBlur={linkPractitionerNpiAvailability.validateOnBlur}
                        placeholder="1234567890"
                        maxLength={12}
                        inputMode="numeric"
                        aria-invalid={linkPractitionerNpiInvalid || linkPractitionerNpiAvailability.hasDuplicate}
                        className={`w-[16ch] rounded-base border bg-white dark:bg-gray-900 px-2 py-2 font-mono text-sm focus:outline-none focus:ring-1 ${
                          linkPractitionerNpiInvalid || linkPractitionerNpiAvailability.hasDuplicate
                            ? 'border-border-error dark:border-error-700 focus:border-border-error dark:focus:border-error-700 focus:ring-error-500'
                            : 'border-border-base-medium dark:border-gray-700 focus:border-brand-600 dark:focus:border-brand-500 focus:ring-brand-600'
                        }`}
                      />
                      {linkPractitionerNpiInvalid && (
                        <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{NPI_INVALID_MESSAGE}</p>
                      )}
                      {linkPractitionerNpiAvailability.isChecking && (
                        <p className="mt-1 text-xs text-text-body dark:text-gray-400">Checking NPI availability…</p>
                      )}
                      {linkPractitionerNpiAvailability.duplicateError && (
                        <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{linkPractitionerNpiAvailability.duplicateError}</p>
                      )}
                      <label className="mt-2 mb-1 block text-xs font-medium text-text-body dark:text-gray-400">Display name (optional)</label>
                      <input
                        type="text"
                        value={linkPractitionerName}
                        onChange={(e) => setLinkPractitionerName(e.target.value)}
                        placeholder={member.userName ?? ''}
                        maxLength={DISPLAY_NAME_MAX_LENGTH}
                        className="w-full rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      />
                      {needsNpiForClinician && (
                        <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">NPI is required for clinician</p>
                      )}
                    </div>
                  )}

                  {hasDelegateRole && (
                    <div className="mt-3 border-t border-border-base dark:border-gray-700 pt-3">
                      <label className="mb-2 block text-xs font-medium text-text-body dark:text-gray-400">
                        Select Clinician to delegate for:
                      </label>
                      <div className="relative">
                        <select
                          value={selectedClinicianId}
                          onChange={(e) => setSelectedClinicianId(e.target.value)}
                          className="w-full appearance-none rounded-base border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-10 py-2 text-sm focus:border-brand-600 dark:focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-600"
                        >
                          <option value="">-- Select Clinician --</option>
                          {availableClinicians.length === 0 ? (
                            <option disabled>No other clinicians available</option>
                          ) : (
                            availableClinicians.map((c) => (
                              <option key={c.userId} value={c.userId}>
                                {clinicianOptionLabel(c, ccsEnabled)}
                              </option>
                            ))
                          )}
                        </select>
                        <ChevronDown
                          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-body dark:text-gray-400"
                          aria-hidden
                        />
                      </div>
                      {needsClinicianSelection && (
                        <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">Required for delegate role</p>
                      )}
                    </div>
                  )}
                </>
              )}
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPendingRoleKeys([])
                    setSelectedClinicianId('')
                    setLinkPractitionerNpi('')
                    setLinkPractitionerName('')
                    setRowRolesMenuOpen(false)
                  }}
                  className="inline-flex items-center rounded border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                >
                  Clear
                </button>
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={addSelectedRoles}
                  disabled={
                    pendingRoleKeys.length === 0 ||
                    needsClinicianSelection ||
                    needsNpiForClinician ||
                    (hasClinicianRole && linkPractitionerNpiInvalid) ||
                    linkPractitionerNpiAvailability.hasDuplicate ||
                    linkPractitionerNpiAvailability.isChecking
                  }
                  className="focus-visible:ring-brand-600"
                >
                  Add selected
                </Button>
              </div>
            </div>
          </details>
        </div>
      </td>
      <td className="px-3 py-2.5 text-right text-xs text-text-body dark:text-gray-400">
        <div className="flex flex-col items-end gap-1.5">
          <div>
            <span className="text-[10px] text-text-body dark:text-gray-400">membership</span>
            <div className="mt-0.5 font-mono text-[10px] text-text-body dark:text-gray-400">{member.membershipId}</div>
          </div>
          <button
            type="button"
            onClick={() => onPurgeUser(member.userId)}
            disabled={Boolean(currentUserId && currentUserId === member.userId)}
            className="inline-flex items-center rounded border border-border-error-subtle dark:border-error-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs font-medium text-text-fg-error dark:text-error-100 hover:bg-error-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            title={currentUserId === member.userId ? 'You cannot delete yourself' : 'Permanently delete user'}
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  )
}
